import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';

const NeoHistoryPage = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, conversationId: null, isAll: false });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/neo/history?limit=100`);
      setConversations(response.data.conversations || []);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch Neo history:', error);
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    try {
      await axios.delete(`${API}/neo/history`);
      setConversations([]);
      setDeleteModal({ isOpen: false, conversationId: null, isAll: false });
    } catch (error) {
      console.error('Failed to delete history:', error);
      alert('Failed to delete conversation history');
    }
  };

  const handleDeleteSingle = async (conversationId) => {
    try {
      await axios.delete(`${API}/neo/history/${conversationId}`);
      setConversations(conversations.filter(c => c.conversation_id !== conversationId));
      setDeleteModal({ isOpen: false, conversationId: null, isAll: false });
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      alert('Failed to delete conversation');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-GB', { 
      day: '2-digit', 
      month: 'short',
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex items-center justify-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600 text-lg">Loading conversation history...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      {/* Header - Matching Dashboard Theme */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Neo Conversation History</h1>
              <p className="text-purple-200 text-sm">View and manage all Neo AI conversations across your platform</p>
            </div>
          </div>
          {conversations.length > 0 && (
            <button
              onClick={() => setDeleteModal({ isOpen: true, conversationId: null, isAll: true })}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shadow-lg font-semibold text-sm"
            >
              Clear All History
            </button>
          )}
        </div>
      </div>

      {/* Stats Card */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-800">{conversations.length}</div>
            <div className="text-sm text-gray-600 mt-1">Total Conversations</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-800">
              {conversations.reduce((sum, c) => sum + (c.credits_used || 0), 0)}
            </div>
            <div className="text-sm text-gray-600 mt-1">Total Credits Used</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-800">
              {conversations.reduce((sum, c) => sum + (c.tokens_used || 0), 0)}
            </div>
            <div className="text-sm text-gray-600 mt-1">Total Tokens</div>
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200">
        <div className="bg-gradient-to-r from-indigo-100 to-purple-100 px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">All Conversations</h2>
          <p className="text-sm text-gray-600 mt-0.5">Recent conversations listed first</p>
        </div>

        <div className="p-6">
          {conversations.length > 0 ? (
            <div className="space-y-3">
              {conversations.map((conv) => (
                <div key={conv.conversation_id} className="bg-gradient-to-r from-white to-purple-50 border-2 border-gray-200 rounded-lg p-4 hover:border-purple-300 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-bold text-gray-900">{conv.username}</span>
                      <span className={`px-3 py-0.5 text-xs font-bold rounded-full ${
                        conv.role === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : conv.role === 'team_member'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {conv.role}
                      </span>
                      <span className="text-xs text-gray-500 font-medium">
                        {formatTimestamp(conv.timestamp)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-purple-100 rounded-lg px-3 py-1 border border-purple-200">
                        <span className="text-xs font-bold text-purple-800">{conv.credits_used} credits</span>
                      </div>
                      <button
                        onClick={() => setDeleteModal({ isOpen: true, conversationId: conv.conversation_id, isAll: false })}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete this conversation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="text-xs font-semibold text-gray-700">User Query:</span>
                      </div>
                      <p className="text-sm text-gray-900">{conv.user_message}</p>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center space-x-2 mb-1">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        <span className="text-xs font-semibold text-blue-700">Neo Response:</span>
                      </div>
                      <p className="text-sm text-gray-700">{conv.ai_response}</p>
                    </div>

                    <div className="flex items-center justify-between text-xs text-gray-500 mt-2">
                      <div className="flex items-center space-x-4">
                        <span>Type: <strong className="text-gray-700">{conv.query_type}</strong></span>
                        <span>Tokens: <strong className="text-gray-700">{conv.tokens_used}</strong></span>
                      </div>
                      {conv.feedback && (
                        <div className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          conv.feedback === 'positive' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {conv.feedback === 'positive' ? '👍 Positive' : '👎 Negative'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
              <p className="text-gray-500 font-medium">No conversation history yet</p>
              <p className="text-gray-400 text-sm mt-1">Start chatting with Neo to see your conversations here</p>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {deleteModal.isAll ? 'Clear All History?' : 'Delete Conversation?'}
            </h3>
            <p className="text-gray-600 mb-6">
              {deleteModal.isAll 
                ? 'This will permanently delete all your Neo conversation history. This action cannot be undone.'
                : 'This will permanently delete this conversation. This action cannot be undone.'
              }
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setDeleteModal({ isOpen: false, conversationId: null, isAll: false })}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteModal.isAll) {
                    handleDeleteAll();
                  } else {
                    handleDeleteSingle(deleteModal.conversationId);
                  }
                }}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-semibold"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NeoHistoryPage;
